import { ISelectOption, TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { TLocalesKeys } from 'localization';
import { IGetWorkersResponse, IWorkShiftsReportResponse } from 'schemas';
import { INormalizedReportItem, TNormalizedSummaryItem } from 'types';

export const formatDuration = (
  duration: number,
  t: TTranslator<TLocalesKeys>
): string => {
  const hours = (duration / 3600) ^ 0;
  const minutes = ((duration - hours * 3600) / 60) ^ 0;

  let res = '';

  if (hours) {
    res += t('time.contracted.hours', hours.toLocaleString());
  }

  if (minutes) {
    if (hours) res += ' ';

    res += t('contracted.minutes', minutes);
  }

  if (!res) {
    res = t('contracted.minutes', '0');
  }

  return res;
};

export const formatMoney = (m: number, cur?: string): string =>
  `${m.toLocaleString(undefined, { maximumFractionDigits: 2 })}${
    cur ? ' ' + cur : ''
  }`;

export const formatWorkerFIO = (fio: string): string => {
  const [first, sur, second] = fio.trim().replaceAll(/\s\s+/g, ' ').split(' ');

  if (!second) return '';

  let tmp = second;

  if (first) {
    tmp += ` ${first[0]}.`;
  }

  if (sur) {
    tmp += ` ${sur[0]}.`;
  }

  return tmp;
};

export const formatDate = (date: string): string =>
  dayjs(date).format('DD.MM.YYYY');

export const getWorkShiftsPeriod = (
  workShifts: Array<INormalizedReportItem>
): string =>
  `${formatDate(workShifts[workShifts.length - 1]?.starts)} - ${formatDate(
    workShifts[0]?.finish
  )}`;

export const getWorkersOptions = (
  workersResponse: IGetWorkersResponse | undefined
): Array<ISelectOption<string>> => {
  if (!workersResponse?.workers.data) return [];

  return workersResponse.workers.data
    .filter(({ last_name }) => !!last_name)
    .map(({ uuid, last_name, first_name }) => ({
      value: uuid,
      title: `${last_name} ${first_name?.[0]}.`,
    }));
};

export const getNormalizeReportData = (
  reportData?: IWorkShiftsReportResponse
): {
  shifts: Array<INormalizedReportItem>;
  summary: Array<TNormalizedSummaryItem>;
  total: Omit<TNormalizedSummaryItem, 'workerFIO'>;
} => {
  const normalizedData: {
    shifts: Array<INormalizedReportItem>;
    summary: Array<TNormalizedSummaryItem>;
    total: Omit<TNormalizedSummaryItem, 'workerFIO'>;
  } = {
    shifts: [],
    summary: [],
    total: {
      bonuses: 0,
      duration: 0,
      goods: 0,
      proceeds: 0,
      refunds: 0,
      services: 0,
      counter: 0,
      tips: 0,
    },
  };

  if (!reportData) return normalizedData;

  normalizedData.shifts = reportData.workShiftsReport.data.map(
    ({ values }, index) => {
      const id = Number(reportData.workShiftsReport.keys[index]);
      const workerFIO = formatWorkerFIO(
        reportData.workShiftsReport.extraLabels[index]
      );
      const starts = reportData.workShiftsReport.extraLabels2[index];
      const finish = reportData.workShiftsReport.extraLabels3[index];
      const duration = values[0] ?? 0;
      const card = values[3] ?? 0;
      const cash = values[4] ?? 0;
      const bonuses = values[9] ?? 0;
      const tips = values[12] ?? 0;

      return {
        id,
        workerFIO,
        starts,
        finish,
        bonuses,
        card,
        cash,
        proceeds: cash + card,
        duration,
        tips,
      };
    }
  );

  normalizedData.summary = reportData.workShiftsSummaryReport.data.map(
    ({ values }, index) => {
      const workerFIO = formatWorkerFIO(
        reportData.workShiftsSummaryReport.keys[index]
      );
      const duration = values[0];
      const refunds = values[1] ?? 0;
      const proceeds = values[2] ?? 0;
      const goods = values[3] ?? 0;
      const services = values[4] ?? 0;
      const bonuses = values[7] ?? 0;
      const counter = values[10] ?? 0;
      const tips = values[11] ?? 0;

      normalizedData.total.bonuses += bonuses;
      normalizedData.total.duration += duration;
      normalizedData.total.goods += goods;
      normalizedData.total.proceeds += proceeds;
      normalizedData.total.refunds += refunds;
      normalizedData.total.services += services;
      normalizedData.total.counter += counter;
      normalizedData.total.tips += tips;

      return {
        workerFIO,
        duration,
        refunds,
        proceeds,
        goods,
        services,
        bonuses,
        counter,
        tips,
      };
    }
  );

  return normalizedData;
};
